import { benchmark } from 'shared/containers/App/saga';
import { call, Effect, put, select, takeLatest } from 'redux-saga/effects';
import { creditVerificationActions } from './slice';
import {
  appActions,
  CreditTag,
  Modals,
  selectCountry,
  selectCustomerType,
  selectErrorModalAttempts,
  selectPartyId,
  selectSalesAgreementId,
  selectUrlParams,
} from '@buy-viasat/redux/src/app';
import { selectCartId, selectOnetimeDiscount } from '@buy-viasat/redux/src/cart';
import performCreditCheckAsync, {
  PerformCreditCheckPayload,
} from '../../providers/requests/customer/performCreditCheck';
import { personalInformationActions } from '../PersonalInformation/slice';
import { navActions, Routes } from '@buy-viasat/redux/src/navigator';
import i18n from '@buy-viasat/utils/src/lib/config/i18n';
import { selectPlanId, selectPlanOfferId, selectValidatedClosingOfferId } from '@buy-viasat/redux/src/plan';
import {
  selectCoordinates,
  selectIsVermontCustomer,
  selectServiceAddressValues,
  selectUnsanitizedAddressLines,
} from '@buy-viasat/redux/src/serviceability';
import { USE_NEW_API } from 'shared/utils/dataFetchingHooks/constants';
import {
  buildEligibleCreditCheckActions,
  buildSkippedCreditCheckActions,
  selectDateOfBirth,
  selectLastCreditCheckSignature,
  selectLastFourOfSSN,
  selectTaxId,
  selectUpdateCreditVerification,
} from '@buy-viasat/redux/src/credit-verification';
import { PerformCreditCheckResponse } from '@buy-viasat/types/build/bv';

export function* checkCustomerCreditSaga() {
  const [
    onetimeDiscount,
    partyId,
    cartId,
    planId,
    serviceAddress,
    coordinates,
    lastFourOfSSN,
    taxId,
    isVermontCustomer,
    updateCreditVerification,
    urlParams,
    dateOfBirth,
    salesAgreementId,
    offerId,
    validatedClosingOfferId,
    customerType,
    country,
    attempts,
    lastCreditCheckSignature,
    unsanitizedAddressLines,
  ] = yield* getCreditCheckSelectors();

  yield put(creditVerificationActions.setIsLoading(true));

  const HAS_NOT_REACHED_MAX_ATTEMPTS = attempts < 2;
  if (!partyId) {
    if (!HAS_NOT_REACHED_MAX_ATTEMPTS) {
      yield put(navActions.routeUserTo(Routes.PERSONAL_INFORMATION));
      yield put(appActions.setErrorModalAttempts(0));
      yield put(creditVerificationActions.setIsLoading(false));
      return;
    }

    yield put(appActions.setModalVisible(Modals.ERROR));
    // Show the error modal and set the retry action in the store.
    // setErrorRetryAction is used so the modal can generically dispatch the correct retry action,
    // regardless of which saga or flow triggered the error. This keeps the modal reusable and decoupled.
    yield put(
      appActions.setErrorRetryAction({ type: creditVerificationActions.checkCustomerCredit.type, payload: null }),
    );
    yield put(creditVerificationActions.setIsLoading(false));
    return;
  }

  const creditCheckInput = {
    partyId,
    cartId,
    productId: planId,
    lastFourOfSSN,
    taxId,
    dateOfBirth,
    serviceAddress,
    unsanitizedAddressLines,
    coordinates,
    network: updateCreditVerification,
    dealerId: urlParams.dealerId,
    partnerId: urlParams.partnerId,
    salesAgreementId,
    offerId,
    validatedClosingOfferId: validatedClosingOfferId ?? '',
    customerType,
    country,
  };

  // Skip the network call if a prior credit check already succeeded with identical inputs.
  // `network` is a change-tracking flag, not real input, so it is excluded from the signature.
  const { network: _network, ...signatureInput } = creditCheckInput;
  const creditCheckSignature = JSON.stringify(signatureInput);

  if (lastCreditCheckSignature === creditCheckSignature) {
    for (const action of buildSkippedCreditCheckActions()) {
      yield put(action);
    }
    return;
  }

  //below are used for masking Total Due Today section
  yield put(creditVerificationActions.setIsLoading(false));
  yield put(creditVerificationActions.setCreditCheckComplete(false));
  yield put(appActions.setDisplayCalculatedLaterCartLabel(true));

  try {
    const {
      data: { performCreditCheck: results },
    }: PerformCreditCheckPayload = yield call(performCreditCheckAsync, creditCheckInput);

    if (!results.isEligible) {
      yield put(appActions.setModalVisible(Modals.CAE));
      yield put(creditVerificationActions.setLastCreditCheckSignature(null));
    } else {
      yield* processEligibleCreditCheckResults(results, onetimeDiscount);
      yield put(creditVerificationActions.setLastCreditCheckSignature(creditCheckSignature));
    }

    yield put(appActions.setCreditCheckResults([{ name: CreditTag.DEFAULT, value: results.creditTag ?? '' }]));
  } catch (e: any) {
    yield put(creditVerificationActions.setLastCreditCheckSignature(null));
    yield* handleCreditCheckError(e, isVermontCustomer);
  } finally {
    yield put(creditVerificationActions.updateCreditVerification(false));
    yield put(personalInformationActions.setIsPersonalInformationLoading(false));
  }
}

function* getCreditCheckSelectors(): Generator<Effect> {
  const onetimeDiscount = yield select(selectOnetimeDiscount);
  const partyId = yield select(selectPartyId);
  const cartId = yield select(selectCartId);
  const planId = yield select(selectPlanId);
  const serviceAddress = yield select(selectServiceAddressValues);
  const coordinates = yield select(selectCoordinates);
  const lastFourOfSSN = yield select(selectLastFourOfSSN);
  const taxId = yield select(selectTaxId);
  const isVermontCustomer = yield select(selectIsVermontCustomer);
  const updateCreditVerification = yield select(selectUpdateCreditVerification);
  const urlParams = yield select(selectUrlParams);
  const dateOfBirth = yield select(selectDateOfBirth);
  const salesAgreementId = yield select(selectSalesAgreementId);
  const offerId = yield select(selectPlanOfferId);
  const validatedClosingOfferId = yield select(selectValidatedClosingOfferId);
  const customerType = yield select(selectCustomerType);
  const country = yield select(selectCountry);
  const attempts = yield select(selectErrorModalAttempts);
  const lastCreditCheckSignature = yield select(selectLastCreditCheckSignature);
  const unsanitizedAddressLines = yield select(selectUnsanitizedAddressLines);
  return [
    onetimeDiscount,
    partyId,
    cartId,
    planId,
    serviceAddress,
    coordinates,
    lastFourOfSSN,
    taxId,
    isVermontCustomer,
    updateCreditVerification,
    urlParams,
    dateOfBirth,
    salesAgreementId,
    offerId,
    validatedClosingOfferId,
    customerType,
    country,
    attempts,
    lastCreditCheckSignature,
    unsanitizedAddressLines,
  ];
}

function* processEligibleCreditCheckResults(results: PerformCreditCheckResponse, onetimeDiscount: number) {
  //installatonFee has always been totalWithoutPromos, we rename it in the frontend so its backwards compatible w/o any schema invalidation
  //in the new backend we keep its actual name intact
  const actions = buildEligibleCreditCheckActions({
    totalWithoutPromos: results?.installationFee,
    feesAndDiscounts: results.feesAndDiscounts,
    onetimeDiscount,
    useNewApi: USE_NEW_API,
  });

  for (const action of actions) {
    yield put(action);
  }
}

function* handleCreditCheckError(error: Error, isVermontCustomer: boolean) {
  if (isVermontCustomer) {
    yield put(navActions.routeUserTo(Routes.PERSONAL_INFORMATION));
  }

  if (error.message === 'Restricted contract terms') {
    yield put(
      appActions.setModalVisibleOverride({
        modal: Modals.ERROR,
        overrideBody: i18n.t('signup:CreditCheck.restrictedContract.body'),
        overrideHeader: i18n.t('signup:CreditCheck.restrictedContract.title'),
      }),
    );
  } else {
    yield put(
      appActions.setErrorRetryAction({ type: creditVerificationActions.checkCustomerCredit.type, payload: null }),
    );
    yield put(appActions.setModalVisible(Modals.ERROR));
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export function* creditVerificationSaga(): Generator {
  yield takeLatest(creditVerificationActions.checkCustomerCredit.type, benchmark(checkCustomerCreditSaga));
}
